import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, RequireAuth } from "@/lib/auth";
import Home from "@/pages/Home";
import Landing from "@/pages/Landing";
import BuilderPage from "@/pages/BuilderPage";
import DevelopmentPage from "@/pages/DevelopmentPage";
import Viewer from "@/pages/Viewer";
import SavedHome from "@/pages/SavedHome";
import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminDevelopments from "@/pages/admin/AdminDevelopments";
import AdminHouseTypes from "@/pages/admin/AdminHouseTypes";
import HomeSetup from "@/pages/admin/HomeSetup";
import AdminEditor from "@/pages/admin/AdminEditor";
import AdminHelp from "@/pages/admin/AdminHelp";
import AdminSkin from "@/pages/admin/AdminSkin";
import AdminAssistant from "@/pages/admin/AdminAssistant";
import AdminFinishes from "@/pages/admin/AdminFinishes";
import AdminRealtors from "@/pages/admin/AdminRealtors";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminSettings from "@/pages/admin/AdminSettings";
import PlatformAdmin from "@/pages/admin/PlatformAdmin";
import TdvDashboard from "@/pages/admin/tdv/TdvDashboard";
import { UnsavedGuard } from "@/lib/unsaved";
import TdvViewer from "@/threedvista/TdvViewer";
import AdminTdvList from "@/threedvista/AdminTdvList";
import AdminTdvEdit from "@/threedvista/AdminTdvEdit";
import TdvHome from "@/threedvista/TdvHome";
import { TdvSignup, TdvLogin, TdvForgot, TdvReset } from "@/threedvista/TdvAuthPages";
import { TdvLayout, TdvBranding, TdvLeads } from "@/threedvista/TdvApp";
import TdvAnalytics from "@/threedvista/TdvAnalytics";
import TdvSkin from "@/threedvista/TdvSkin";
import TdvPlan, { PaymentSuccess, PaymentCancel } from "@/threedvista/TdvPlan";
import TdvAssistant from "@/threedvista/TdvAssistant";
import G360KeyPage from "@/threedvista/G360Connect";
import TdvDemo from "@/threedvista/TdvDemo";
import GiraffeHome from "@/threedvista/GiraffeHome";
import GiraffeDemo from "@/threedvista/GiraffeDemo";

const tourApp = (base) => (
  <Route path={`${base}/app`} element={<TdvLayout />}>
    <Route index element={<AdminTdvList />} />
    <Route path="tours/:id" element={<AdminTdvEdit />} />
    <Route path="branding" element={<TdvBranding />} />
    <Route path="leads" element={<TdvLeads />} />
    <Route path="analytics" element={<TdvAnalytics />} />
    <Route path="skin" element={<TdvSkin />} />
    <Route path="plan" element={<TdvPlan />} />
    <Route path="assistant" element={<TdvAssistant />} />
    <Route path="connect" element={<G360KeyPage />} />
  </Route>
);

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <UnsavedGuard />
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/homes" element={<Home />} />
            <Route path="/embed/:id" element={<Viewer embed />} />
            <Route path="/b/:slug" element={<BuilderPage />} />
            <Route path="/d/:id" element={<DevelopmentPage />} />
            <Route path="/view/:id" element={<Viewer />} />
            <Route path="/try/:id" element={<Viewer tryMode />} />
            <Route path="/3d/:id" element={<TdvViewer />} />
            <Route path="/3dvista" element={<TdvHome />} />
            <Route path="/3dvista/try" element={<TdvDemo />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/3dvista/signup" element={<TdvSignup />} />
            <Route path="/3dvista/login" element={<TdvLogin />} />
            <Route path="/3dvista/forgot" element={<TdvForgot />} />
            <Route path="/3dvista/reset" element={<TdvReset />} />
            {tourApp("/3dvista")}
            <Route path="/giraffe" element={<GiraffeHome />} />
            <Route path="/giraffe/try" element={<GiraffeDemo />} />
            <Route path="/giraffe/signup" element={<TdvSignup />} />
            <Route path="/giraffe/login" element={<TdvLogin />} />
            <Route path="/giraffe/forgot" element={<TdvForgot />} />
            <Route path="/giraffe/reset" element={<TdvReset />} />
            {tourApp("/giraffe")}
            <Route path="/home/:sid" element={<SavedHome />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<RequireAuth><AdminLayout /></RequireAuth>}>
              <Route index element={<AdminAnalytics />} />
              <Route path="developments" element={<AdminDevelopments />} />
              <Route path="house-types" element={<AdminHouseTypes />} />
              <Route path="scans" element={<Navigate to="/admin/house-types" replace />} />
              <Route path="homes/:id" element={<HomeSetup />} />
              <Route path="editor" element={<AdminEditor />} />
              <Route path="help" element={<AdminHelp />} />
              <Route path="skin" element={<AdminSkin />} />
              <Route path="assistant" element={<AdminAssistant />} />
              <Route path="finishes" element={<AdminFinishes />} />
              <Route path="realtors" element={<AdminRealtors />} />
              <Route path="leads" element={<AdminLeads />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route path="platform" element={<PlatformAdmin />} />
              <Route path="platform/3dvista" element={<TdvDashboard />} />
              <Route path="platform/giraffe" element={<TdvDashboard key="giraffe" product="giraffe" />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
